import { GoogleUser, User } from '../types';

/**
 * Main Google Authentication function
 * This should be called from within the GoogleOAuthProvider context
 * @returns Promise with user information
 */
export async function googleAuth(): Promise<User> {
  throw new Error(
    'googleAuth() should not be called directly. Use the AuthModal component or useGoogleLogin hook from @react-oauth/google instead.'
  );
}

/**
 * Fetches user information from Google using the access token
 * @param accessToken - The access token from Google OAuth
 * @returns Promise with user information
 */
export async function fetchGoogleUserInfo(
  accessToken: string
): Promise<GoogleUser> {
  try {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch user info from Google');
    }

    const data = await response.json();

    // Map Google response to our User interface
    const user: GoogleUser = {
      id: data.sub, // Use Google's sub as the id
      email: data.email,
      name: data.name,
      picture: data.picture,
      sub: data.sub,
    };

    return user;
  } catch (error) {
    console.error('Error fetching Google user info:', error);
    throw error;
  }
}

/**
 * Validates if the Google Client ID is properly formatted
 * @param clientId - The Google OAuth Client ID
 * @returns boolean indicating if the client ID is valid
 */
export function validateGoogleClientId(clientId: string): boolean {
  if (!clientId || typeof clientId !== 'string') {
    return false;
  }

  // Google Client IDs typically end with .apps.googleusercontent.com
  const googleClientIdPattern = /^[\w-]+\.apps\.googleusercontent\.com$/;
  return googleClientIdPattern.test(clientId);
}

/**
 * Stores user information in session storage (optional utility)
 * @param user - The user object
 */
export function storeUserSession(user: User): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('gamepass_user', JSON.stringify(user));
    }
  } catch (error) {
    console.warn('Could not store user session:', error);
  }
}

/**
 * Retrieves user information from session storage (optional utility)
 * @returns User object or null
 */
export function getUserSession(): User | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const userStr = sessionStorage.getItem('gamepass_user');
      if (userStr) {
        return JSON.parse(userStr) as User;
      }
    }
  } catch (error) {
    console.warn('Could not retrieve user session:', error);
  }
  return null;
}

/**
 * Clears user session from storage
 */
export function clearUserSession(): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('gamepass_user');
    }
  } catch (error) {
    console.warn('Could not clear user session:', error);
  }
}

/**
 * Handles OAuth errors and provides user-friendly messages
 * @param error - The error object
 * @returns User-friendly error message
 */
export function handleAuthError(error: any): string {
  if (!error) {
    return 'An unknown error occurred during authentication';
  }

  // Handle specific Google OAuth errors
  if (error.error === 'popup_closed_by_user') {
    return 'Authentication was cancelled';
  }

  if (error.error === 'access_denied') {
    return 'Access was denied. Please try again.';
  }

  if (error.error === 'invalid_client') {
    return 'Invalid Google Client ID. Please check your configuration.';
  }

  if (error.message) {
    return error.message;
  }

  return 'Authentication failed. Please try again.';
}

/**
 * Checks if the user is authenticated
 * @returns boolean indicating authentication status
 */
export function isAuthenticated(): boolean {
  return getUserSession() !== null;
}

/**
 * Exports the current user's basic info for logging/debugging
 * @param user - The Google user object
 * @returns Sanitized user info without sensitive data
 */
export function getSanitizedUserInfo(user: GoogleUser): {
  name: string;
  email: string;
} {
  return {
    name: user.name,
    email: user.email,
  };
}